/**
 * Every strategy a workspace holds, in one list.
 *
 * A workspace's strategy can be any of five kinds — a classic strategy, an
 * opportunity tree, a kernel, a Strategy Canvas, a V2MOM — each in its own
 * store. Anything that lets a person pick "a strategy" reads this list, so it
 * offers all of them and says which kind each one is.
 */

export type WorkspaceStrategyKind = 'classic' | 'ost' | 'kernel' | 'canvas' | 'v2mom';

/** The kind in plain words, as it is shown beside a strategy's name. */
export const WORKSPACE_STRATEGY_KIND_LABELS: Record<WorkspaceStrategyKind, string> = {
  classic: 'Strategy',
  ost: 'Opportunity tree',
  kernel: 'Strategy kernel',
  canvas: 'Strategy Canvas',
  v2mom: 'V2MOM',
};

export interface WorkspaceStrategyOption {
  id: string;
  name: string;
  kind: WorkspaceStrategyKind;
  /** Where this strategy opens. */
  href: string;
}

/** Where a strategy of a given kind opens. */
export function workspaceStrategyHref(kind: WorkspaceStrategyKind, id: string): string {
  switch (kind) {
    case 'classic': return `/prioritization/strategy/${id}`;
    case 'ost':     return `/prioritization/strategy/opportunity-tree/${id}`;
    case 'kernel':  return `/prioritization/strategy/kernel/${id}`;
    case 'canvas':  return `/prioritization/strategy/canvas/${id}`;
    case 'v2mom':   return `/prioritization/strategy/v2mom/${id}`;
  }
}

interface Named { id: string; name?: string | null }
interface Titled { id: string; title?: string | null }

export interface WorkspaceStrategySources {
  classic?: readonly Named[];
  ostTrees?: readonly Named[];
  kernels?: readonly Named[];
  canvases?: readonly Titled[];
  v2moms?: readonly Titled[];
}

function option(kind: WorkspaceStrategyKind, id: string, raw: string | null | undefined): WorkspaceStrategyOption {
  // A strategy nobody has named yet is still a strategy; it is listed as what
  // it is rather than as a blank row.
  const name = raw?.trim() || `Untitled ${WORKSPACE_STRATEGY_KIND_LABELS[kind].toLowerCase()}`;
  return { id, name, kind, href: workspaceStrategyHref(kind, id) };
}

/** One list from the five stores, ordered by name. */
export function buildWorkspaceStrategyOptions(sources: WorkspaceStrategySources): WorkspaceStrategyOption[] {
  const out: WorkspaceStrategyOption[] = [
    ...(sources.classic ?? []).map((s) => option('classic', s.id, s.name)),
    ...(sources.ostTrees ?? []).map((s) => option('ost', s.id, s.name)),
    ...(sources.kernels ?? []).map((s) => option('kernel', s.id, s.name)),
    ...(sources.canvases ?? []).map((s) => option('canvas', s.id, s.title)),
    ...(sources.v2moms ?? []).map((s) => option('v2mom', s.id, s.title)),
  ];
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** How a strategy reads in a picklist: its name, then its kind. */
export function workspaceStrategyOptionLabel(o: Pick<WorkspaceStrategyOption, 'name' | 'kind'>): string {
  return `${o.name} — ${WORKSPACE_STRATEGY_KIND_LABELS[o.kind]}`;
}
